import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, Newspaper, ArrowRight, Lightning } from '@phosphor-icons/react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_ORIGIN = 'https://servermemoryrfq.com';

const CATEGORY_LABELS = {
  'server-memory': 'Server Memory',
  'ai-memory': 'AI & HBM',
  'datacenter': 'Datacenter',
  'supply-chain': 'Supply Chain',
  'pricing': 'Pricing',
  'manufacturer': 'Manufacturers',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
    return `${Math.floor(diff / (86400 * 30))}mo ago`;
  } catch (e) { return ''; }
};

const upsertHead = (tag, attrs) => {
  const sel = Object.entries(attrs).filter(([k]) => k !== 'content' && k !== 'href')
    .map(([k, v]) => `[${k}="${v}"]`).join('');
  let el = document.head.querySelector(`${tag}${sel}`);
  if (!el) { el = document.createElement(tag); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

const NewsListPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('');
  const [skip, setSkip] = useState(0);
  const LIMIT = 20;

  useEffect(() => {
    document.title = 'Server Memory, AI & Datacenter News | ServerMemoryRFQ';
    upsertHead('meta', { name: 'description', content: 'Latest news on server memory, HBM, DDR5, DDR4, AI chip RAM, and datacenter infrastructure — curated for IT procurement teams and AI engineers by ServerMemoryRFQ.' });
    upsertHead('link', { rel: 'canonical', href: `${SITE_ORIGIN}/news` });
    upsertHead('meta', { property: 'og:title', content: 'Server Memory, AI & Datacenter News | ServerMemoryRFQ' });
    upsertHead('meta', { property: 'og:url', content: `${SITE_ORIGIN}/news` });
    upsertHead('meta', { property: 'og:type', content: 'website' });
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: LIMIT, skip });
    if (category) params.set('category', category);
    axios.get(`${API_URL}/api/news?${params.toString()}`)
      .then(res => { setItems(res.data.items || []); setTotal(res.data.total || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [category, skip]);

  return (
    <div className="min-h-screen" data-testid="news-list-page">
      <Header />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Newspaper size={32} className="text-cyan-400" weight="duotone" />
            <h1 className="text-4xl sm:text-5xl font-['JetBrains_Mono'] font-bold text-white">News</h1>
          </div>
          <p className="text-[#94A3B8] max-w-2xl">
            Daily coverage of server memory, HBM, AI accelerator RAM, and datacenter infrastructure — curated for IT procurement and infrastructure teams.
          </p>
        </div>

        {/* Category filter */}
        <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="category-filter">
          <button
            onClick={() => { setCategory(''); setSkip(0); }}
            className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
              category === '' ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300' : 'border-[#334155] text-[#94A3B8] hover:border-[#94A3B8]'
            }`}
            data-testid="category-all"
          >All</button>
          {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
            <button
              key={k}
              onClick={() => { setCategory(k); setSkip(0); }}
              className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                category === k ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300' : 'border-[#334155] text-[#94A3B8] hover:border-[#94A3B8]'
              }`}
              data-testid={`category-${k}`}
            >{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-center text-[#94A3B8] font-mono py-16">Loading news…</div>
        ) : items.length === 0 ? (
          <div className="text-center text-[#94A3B8] font-mono py-16" data-testid="news-empty">
            No articles yet. Fresh headlines are pulled every hour.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="news-grid">
            {items.map((a) => (
              <Link
                key={a.id}
                to={`/news/${a.slug}`}
                className="block bg-[#11141D] border border-[#1E293B] hover:border-cyan-500/50 rounded-lg p-5 transition-colors group"
                data-testid={`news-card-${a.slug}`}
              >
                <div className="flex items-center gap-2 mb-3 text-xs font-mono uppercase tracking-wider">
                  <span className="text-cyan-400">{a.source_domain || a.source_name}</span>
                  <span className="text-[#334155]">·</span>
                  <Clock size={12} className="text-[#94A3B8]" />
                  <span className="text-[#94A3B8]">{timeAgo(a.published_at)}</span>
                  {a.category && (
                    <>
                      <span className="text-[#334155]">·</span>
                      <span className="text-[#007AFF]">{CATEGORY_LABELS[a.category] || a.category}</span>
                    </>
                  )}
                </div>
                <h2 className="text-xl font-['JetBrains_Mono'] font-bold text-white mb-2 group-hover:text-cyan-300 transition-colors line-clamp-3">
                  {a.title}
                </h2>
                <p className="text-sm text-[#94A3B8] line-clamp-3 mb-3">{a.excerpt}</p>
                <div className="text-xs font-mono text-cyan-400 group-hover:text-cyan-300 flex items-center gap-1">
                  Read more <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {total > LIMIT && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#1E293B]" data-testid="news-pagination">
            <Button
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - LIMIT))}
              className="bg-[#1E293B] hover:bg-[#334155] disabled:opacity-40 font-mono"
              data-testid="prev-page-btn"
            >← Previous</Button>
            <div className="text-xs font-mono text-[#94A3B8]">
              {skip + 1}–{Math.min(skip + LIMIT, total)} of {total}
            </div>
            <Button
              disabled={skip + LIMIT >= total}
              onClick={() => setSkip(skip + LIMIT)}
              className="bg-[#1E293B] hover:bg-[#334155] disabled:opacity-40 font-mono"
              data-testid="next-page-btn"
            >Next →</Button>
          </div>
        )}

        {/* SEO body copy */}
        <section className="mt-16 pt-10 border-t border-[#1E293B]">
          <h2 className="text-xl font-['JetBrains_Mono'] font-bold text-white mb-3">Why ServerMemoryRFQ covers memory news</h2>
          <div className="prose prose-invert max-w-none text-[#94A3B8] text-sm">
            <p>
              Server memory supply, DDR5 and DDR4 pricing, HBM capacity for AI accelerators, and
              datacenter infrastructure moves all directly affect buying decisions. We track the
              stories that matter to procurement teams, system integrators, and AI infrastructure
              engineers — then pair every headline with a short editorial take and a quick path to
              check <Link to="/inventory" className="text-cyan-400">live inventory</Link> or
              <Link to="/rfq" className="text-cyan-400"> request a quote</Link>.
            </p>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default NewsListPage;
